import PropTypes from "prop-types";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  Link,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import SidebarResponsive from "components/Sidebar/Sidebar";
import AdminNavbarLinks from "components/Navbars/AdminNavbarLinks";
import { AuthContext } from "contexts/AuthContext";
import { SidebarContext } from "contexts/SidebarContext";
import { appRoutes } from "routes";
import { hasPermission } from "utils/permission";

function filterRoutesByPermission(routes, user) {
  if (!routes || !Array.isArray(routes)) return [];
  return routes
    .filter((route) => {
      if (route.permissions && !hasPermission({ userPermissions: user?.profilesPermissions, requiredPermissions: route.permissions })) {
        return false;
      }
      return true;
    })
    .map((route) => ({
      ...route,
      items: route.items?.length ? filterRoutesByPermission(route.items, user) : route.items,
    }));
}

export default function AdminNavbar(props) {
  const { variant, children, fixed, secondary, brandText, onOpen, ...rest } = props;
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const { getUserData } = useContext(AuthContext);
  const user = getUserData();
  const { routeStack, breadcrumbKeyParams } = useContext(SidebarContext);
  const PENDING_PARAMS_REGEX = /\{\{[a-zA-Z][a-zA-Z0-9]*\}\}/;

  const sidebarRoutes = useMemo(() => filterRoutesByPermission(appRoutes, user), [user]);

  useEffect(() => {
    window.addEventListener("scroll", changeNavbar);

    return () => {
      window.removeEventListener("scroll", changeNavbar);
    };
  });

  // Here are all the props that may change depending on navbar's type or state.(secondary, variant, scrolled)
  let mainText = fixed && scrolled ? useColorModeValue("gray.700", "gray.200")
    : useColorModeValue("white", "gray.200");
  let secondaryText = fixed && scrolled ? useColorModeValue("gray.700", "gray.200")
    : useColorModeValue("white", "gray.200");
  let navbarPosition = "absolute";
  let navbarFilter = "none";
  let navbarBackdrop = "none";
  let navbarShadow = "none";
  let navbarBg = "none";
  let navbarBorder = "transparent";
  let secondaryMargin = "0px";
  let paddingX = "15px";
  if (props.fixed === true)
    if (scrolled === true) {
      navbarPosition = "fixed";
      navbarShadow = useColorModeValue(
        "0px 7px 23px rgba(0, 0, 0, 0.05)",
        "none"
      );
      navbarBg = useColorModeValue(
        "linear-gradient(112.83deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.8) 110.84%)",
        "linear-gradient(112.83deg, rgba(255, 255, 255, 0.21) 0%, rgba(255, 255, 255, 0) 110.84%)"
      );
      navbarBorder = useColorModeValue("#FFFFFF", "rgba(255, 255, 255, 0.31)");
      navbarFilter = useColorModeValue(
        "none",
        "drop-shadow(0px 7px 23px rgba(0, 0, 0, 0.05))"
      );
    }
  if (props.secondary) {
    navbarBackdrop = "none";
    navbarPosition = "absolute";
    mainText = "white";
    secondaryText = "white";
    secondaryMargin = "22px";
    paddingX = "30px";
  }
  const changeNavbar = () => {
    if (window.scrollY > 1) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };

  const getLocalizedName = (name) => {
    const localized = name.includes(".title") ? t(name, breadcrumbKeyParams) : name;
    return PENDING_PARAMS_REGEX.test(localized) ? null : localized.trim();
  }

  const buildHref = (route) => {
    let href = (route.layout ?? "") + route.path;
    if (route.queryParams && route.includeQueryParams !== false) {
      const params = new URLSearchParams(route.queryParams).toString();
      href += `?${params}`;
    }
    return href;
  };

  return (
    <Flex
      minH="75px"
      left="30px"
      right="30px"
      top="18px"
      borderRadius="16px"
      lineHeight="25.6px"
      mt={secondaryMargin}
      mx={{ sm: paddingX, md: "30px" }}
      bg={navbarBg}
      filter={navbarFilter}
      position={navbarPosition}
      boxShadow={navbarShadow}
      borderColor={navbarBorder}
      backdropFilter={navbarBackdrop}
      borderWidth="1.5px"
      borderStyle="solid"
      flexDirection="column"
    >
      <Flex
        w="100%"
        justify="space-between"
        align="center"
      >
        <SidebarResponsive secondary={props.secondary} routes={sidebarRoutes} />

        <AdminNavbarLinks
          logotext={props.logoText}
          secondary={props.secondary}
          fixed={props.fixed}
          scrolled={scrolled}
        />
      </Flex>

      <Box w="100%" mt="8px" h="100%">
        {routeStack?.length > 0 ? (
          <>
            <Breadcrumb>
              {routeStack.map((route, key) => (
                <BreadcrumbItem key={key} color={mainText} textShadow="1px 1px 4px rgba(0,0,0,0.6)">
                  {route.name === routeStack[routeStack.length - 1]?.name ? (
                    <Text whiteSpace="nowrap">
                      {getLocalizedName(route.name)}
                    </Text>
                  ) : (
                    <BreadcrumbLink href={buildHref(route)}>
                      {getLocalizedName(route.name)}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              ))}
            </Breadcrumb>

            <Text
              bg="inherit"
              borderRadius="inherit"
              fontWeight="bold"
              color={mainText}
              textShadow="1px 1px 4px rgba(0,0,0,0.6)"
            >
              {getLocalizedName(brandText)}
            </Text>
          </>
        ) : (
          <>
            <Breadcrumb>
              <BreadcrumbItem color={mainText}>
                <BreadcrumbLink href="#" color={secondaryText}>
                  Pages
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem color={mainText}>
                <BreadcrumbLink href="#" color={mainText}>
                  {brandText}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>

            <Link
              color={mainText}
              href="#"
              bg="inherit"
              borderRadius="inherit"
              fontWeight="bold"
              textShadow="1px 1px 4px rgba(0,0,0,0.6)"
              _hover={{ color: { mainText } }}
            >
              {brandText}
            </Link>
          </>
        )}
      </Box>
    </Flex>
  );
}

AdminNavbar.propTypes = {
  brandText: PropTypes.string,
  variant: PropTypes.string,
  secondary: PropTypes.bool,
  fixed: PropTypes.bool,
  onOpen: PropTypes.func,
};
